/** Keys on checkout component state that hold cart line collections. */
export const CHECKOUT_LINE_KEYS = [
	'items',
	'products',
	'consignments',
	'services',
	'trades',
	'layaways',
	'work_orders',
];

/**
 * True when the checkout state has at least one line in any cart collection.
 * @param {object} state Checkout component state (or subset with line arrays).
 * @param {string[]} [exclude=[]] Line keys to ignore, e.g. ['trades'].
 */
export function checkoutHasLines(state, exclude = []) {
	const skip = new Set(exclude);
	for (const key of CHECKOUT_LINE_KEYS) {
		if (skip.has(key)) {
			continue;
		}
		const lines = state?.[key];
		if (Array.isArray(lines) && lines.length > 0) {
			return true;
		}
	}
	return false;
}

/**
 * Badge / session count for sidenav cart icon — mirrors server checkoutCartLineCount.
 * @param {object} state Checkout component state.
 * @param {string[]} [exclude=[]] Line keys to ignore.
 */
export function countCheckoutLines(state, exclude = []) {
	const skip = new Set(exclude);
	let total = 0;
	for (const key of CHECKOUT_LINE_KEYS) {
		if (skip.has(key)) {
			continue;
		}
		const lines = state?.[key];
		if (!Array.isArray(lines)) {
			continue;
		}
		if (key === 'items' || key === 'consignments' || key === 'services' || key === 'trades') {
			lines.forEach((line) => {
				total += parseInt(line?.quantity, 10) || 1;
			});
		} else {
			total += lines.length;
		}
	}
	return total;
}

/** Amount to prefill a payment field on focus (always positive for disbursements). */
export function checkoutPaymentFillAmount(totals) {
	const remaining = parseFloat(totals?.remaining);
	const amount = parseFloat(totals?.amount);
	if (!Number.isFinite(remaining)) {
		return '0.00';
	}
	if (amount < 0) {
		return Math.abs(remaining).toFixed(2);
	}
	return remaining.toFixed(2);
}

const CHECKOUT_PAYMENT_FIELDS = [
	'cash_amount',
	'check_amount',
	'zelle_amount',
	'other_amount',
	'invoice_amount',
	'card_amount',
];

const TOLERANCE = 0.005;

export { TOLERANCE };

export function parsePaymentAmount(value) {
	const n = parseFloat(value);
	if (!Number.isFinite(n)) {
		return 0;
	}
	return Math.abs(n);
}

/** Invoice tender only — negative values are never allowed. */
export function parseInvoiceAmount(value) {
	const n = parseFloat(value);
	if (!Number.isFinite(n) || n < 0) {
		return 0;
	}
	return n;
}

export function checkoutPaymentFieldAmount(field, value) {
	if (field === 'invoice_amount') {
		return parseInvoiceAmount(value);
	}
	return parsePaymentAmount(value);
}

/** To Invoice is only valid when the checkout total is a positive sale. */
export function checkoutInvoiceAllowed(totals) {
	const amount = parseFloat(totals?.amount);
	return Number.isFinite(amount) && amount > TOLERANCE;
}

export function isUnsetPaymentAmount(value) {
	if (value === '' || value === undefined || value === null) {
		return true;
	}
	const n = parseFloat(value);
	return !Number.isFinite(n) || n <= TOLERANCE;
}

/** Keep payment amounts as currency strings for the form validator. */
export function formatCheckoutPaymentAmount(value) {
	const n = parseFloat(value);
	if (!Number.isFinite(n) || n < 0) {
		return '0.00';
	}
	return n.toFixed(2);
}

/** At least one payment method panel is open. */
/** Layaway / work-order payment in cart or contract checkout mode. */
export function checkoutIsContractPayment(state) {
	return !!(
		state?.layaway ||
		state?.work_order ||
		(Array.isArray(state?.layaways) && state.layaways.length > 0) ||
		(Array.isArray(state?.work_orders) && state.work_orders.length > 0)
	);
}

export function checkoutPaymentMethodSelected(state) {
	return !!(
		state?.showCashField ||
		state?.showCheckField ||
		state?.showZelleField ||
		state?.showGiftCertField ||
		state?.showInvoiceField ||
		state?.showCreditCardButtons ||
		state?.showDebitCardButtons
	);
}

/** True when To Invoice is in use (panel open or amount entered). */
export function checkoutInvoiceTenderActive(state) {
	if (state?.showInvoiceField === true) {
		return true;
	}
	return parseInvoiceAmount(state?.invoice_amount) > TOLERANCE;
}

/** Max invoice amount allowed without exceeding the transaction total. */
export function checkoutMaxInvoiceAmount(state, totals) {
	const amount = parseFloat(totals?.amount);
	if (!Number.isFinite(amount) || amount < -TOLERANCE) {
		return 0;
	}
	const otherPayments = CHECKOUT_PAYMENT_FIELDS.reduce((sum, field) => {
		if (field === 'invoice_amount') {
			return sum;
		}
		return sum + checkoutPaymentFieldAmount(field, state?.[field]);
	}, 0);
	return Math.max(0, amount - otherPayments);
}

/** Sum of entered payment amounts (always non-negative). */
export function checkoutPaymentsTotal(state) {
	return CHECKOUT_PAYMENT_FIELDS.reduce(
		(sum, field) => sum + checkoutPaymentFieldAmount(field, state?.[field]),
		0
	);
}

/**
 * True when the customer can complete checkout:
 * - A payment method is selected
 * - Sales: tender meets or exceeds amount due (overpay / change allowed)
 * - Disbursements: tender meets amount to give, OR cash/check checkout with $0 (cash: drawer pays full amount)
 */
export function checkoutPaymentsBalanced(state, totals) {
	if (!checkoutPaymentMethodSelected(state)) {
		return false;
	}

	const amount = parseFloat(totals?.amount);
	const remaining = parseFloat(totals?.remaining);
	if (!Number.isFinite(amount) || !Number.isFinite(remaining)) {
		return false;
	}

	if (amount >= -TOLERANCE) {
		if (remaining > TOLERANCE) {
			return false;
		}
		// To Invoice is not cash — do not allow change / over-tender when it is selected.
		if (checkoutInvoiceTenderActive(state) && remaining < -TOLERANCE) {
			return false;
		}
		return true;
	}

	const due = Math.abs(amount);
	const paidOut = checkoutPaymentsTotal(state);

	if (paidOut + TOLERANCE >= due) {
		return true;
	}

	// Trade-in / disbursement: $0 in the field is OK when cash, check, or zelle is selected.
	if ((state?.showCashField || state?.showCheckField || state?.showZelleField) && paidOut < TOLERANCE) {
		return true;
	}

	return remaining >= -TOLERANCE;
}

/** Label + display amount for the balance line (collect, give, or change). */
export function checkoutBalanceDisplay(totals, state = null) {
	const amount = parseFloat(totals?.amount);
	const remaining = parseFloat(totals?.remaining);
	if (!Number.isFinite(remaining)) {
		return { label: 'Amount to Collect', value: '0.00' };
	}
	if (amount >= -TOLERANCE && remaining < -TOLERANCE && state && checkoutInvoiceTenderActive(state)) {
		return { label: 'To Invoice cannot exceed amount due', value: Math.abs(remaining).toFixed(2) };
	}
	if (amount >= -TOLERANCE && remaining < -TOLERANCE) {
		return { label: 'Change', value: Math.abs(remaining).toFixed(2) };
	}
	if (amount < -TOLERANCE) {
		const give = Math.abs(remaining) < TOLERANCE ? '0.00' : Math.abs(remaining).toFixed(2);
		return { label: 'Amount to Give', value: give };
	}
	const collect = Math.abs(remaining) < TOLERANCE ? '0.00' : remaining.toFixed(2);
	return { label: 'Amount to Collect', value: collect };
}
